import { getRuntimeEnv } from './env';

function isPlainObject(v) {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

/**
 * Overlay wins for non-empty values; empty string in overlay does not wipe base.
 * @param {object|null|undefined} base
 * @param {object|null|undefined} overlay
 */
function mergePublicConfig(base, overlay) {
  const out = isPlainObject(base) ? { ...base } : {};
  if (!isPlainObject(overlay)) {
    return Object.keys(out).length ? out : null;
  }
  for (const [k, v] of Object.entries(overlay)) {
    if (v === null || v === undefined) continue;
    if (typeof v === 'string' && v.trim() === '') continue;
    out[k] = v;
  }
  return Object.keys(out).length ? out : null;
}

async function fetchJson(url) {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

async function loadPublicAppConfigUncached() {
  const apiUrl = process.env.REACT_APP_SKIP_CONFIG_API_URL;
  if (apiUrl && !apiUrl.startsWith('mock:')) {
    const cfg = await fetchJson(apiUrl);
    return cfg && typeof cfg === 'object' && !Array.isArray(cfg) ? cfg : null;
  }

  const env = getRuntimeEnv();
  const base = await fetchJson('/config.json');
  const envCfg = await fetchJson(`/config.${env}.json`);
  const merged = mergePublicConfig(base, envCfg);
  if (merged) return merged;
  return envCfg || base || null;
}

// The config lives in static JSON that never changes for the life of a page.
// Home's OAuth bootstrap, the OAuth callback, and the verification modal's
// onInit each need it, so without this every one of them (doubled again by
// StrictMode in dev) refetched /config.json + /config.{env}.json — up to ~4
// real round-trips in prod before the redirect decision and the form could be
// shown. Cache the in-flight promise so concurrent and later callers share one
// fetch. Failed loads (null / thrown) are not cached, so a transient blip on
// the first call doesn't wedge the whole session on a bad config.
let cachedConfigPromise = null;

/**
 * Loads the same public JSON config as Home's getSkipConfig (skip URL override respected).
 * For local dev, merges `/config.json` with `/config.{env}.json` so empty strings in
 * `config.development.json` (e.g. starfleetClientId) do not hide values only in `config.json`.
 * Result is memoized for the page lifetime (see cachedConfigPromise).
 * @returns {Promise<object|null>}
 */
export function loadPublicAppConfig() {
  if (cachedConfigPromise) return cachedConfigPromise;
  cachedConfigPromise = loadPublicAppConfigUncached().then(
    (cfg) => {
      if (cfg == null) cachedConfigPromise = null;
      return cfg;
    },
    (err) => {
      cachedConfigPromise = null;
      throw err;
    }
  );
  return cachedConfigPromise;
}

/**
 * Test seam: drops the memoized config so each test can install its own fetch
 * mock and observe a fresh load. Not used in production code.
 */
export function __resetPublicAppConfigCache() {
  cachedConfigPromise = null;
}
